import { SeoHead } from "@/components/SeoHead"
import { MarketingLayout } from "@/components/marketing/MarketingLayout"
import { Section, Eyebrow } from "@/components/marketing/sections/Section"
import { SeoAuditForm } from "@/components/marketing/SeoAuditForm"
import { Button } from "@/components/ui/button"
import { track } from "@/lib/analytics"

const DESCRIPTION =
  "Local SEO for service businesses — audit and fix an existing site, or build a new one optimized to show up when locals search for what you do."

const HAVE_A_SITE = [
  "Technical SEO audit — security, speed, schema, crawl issues, broken indexing",
  "On-page optimization for your services and the towns you serve",
  "Google Business Profile setup and ongoing optimization",
  "Citation cleanup, monitoring, and ranking reports",
]

const NEED_A_SITE = [
  "Custom site built around your service area and specialties",
  "Location pages for every town you serve, not just one generic page",
  "Conversion-focused design with the trust signals locals look for",
  "Full technical SEO baked in from day one — speed, schema, security",
]

export default function Seo() {
  const handleInquiry = () => track("SEO Inquiry Clicked")

  return (
    <MarketingLayout>
      <SeoHead title="Local SEO" description={DESCRIPTION} />

      {/* Hero */}
      <Section containerClassName="max-w-3xl">
        <Eyebrow>FullCast · Local SEO</Eyebrow>
        <h1 className="display-1 mt-4">Local SEO for service businesses</h1>
        <p className="mt-5 text-lg text-ink-body">
          Helping contractors, trades, home services, marine, landscaping, and
          similar service businesses show up when locals search for what they
          do. Audit and fix the site you have, or build one from scratch.
        </p>
      </Section>

      {/* Free audit — primary CTA */}
      <Section className="bg-surface" containerClassName="max-w-3xl">
        <Eyebrow>Start here · 60 seconds</Eyebrow>
        <h2 className="display-2 mt-4">Run a free audit of your website</h2>
        <p className="mt-5 text-lg text-ink-body">
          Enter your address and I'll grade the things that quietly cost you
          customers — broken certificates, hidden hacks, mobile problems,
          missing Google signals. Ten checks, A to F, results on the next
          page. No call required, no strings.
        </p>

        <div className="mt-10 rounded-2xl border border-hairline bg-page p-6 sm:p-8">
          <SeoAuditForm source="seo_page" ctaLabel="Run my free audit" />
        </div>
      </Section>

      {/* Two paths */}
      <Section containerClassName="max-w-4xl">
        <Eyebrow>How it works</Eyebrow>
        <h2 className="display-2 mt-4">I meet you where you are</h2>
        <p className="mt-3 text-ink-muted">
          Not every business needs a new site. Some need the one they have to
          finally work. Others need to start over. Either way, the goal is the
          same: show up first when a local customer searches.
        </p>

        <div className="mt-10 grid gap-6 md:grid-cols-2">
          <div className="rounded-xl border border-hairline bg-page p-6">
            <h3>If you have a site</h3>
            <ul className="mt-4 space-y-3 text-ink-body">
              {HAVE_A_SITE.map((item) => (
                <li key={item} className="flex gap-2">
                  <span className="mt-2 inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-accent" aria-hidden="true" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="rounded-xl border border-hairline bg-page p-6">
            <h3>If you need a site</h3>
            <ul className="mt-4 space-y-3 text-ink-body">
              {NEED_A_SITE.map((item) => (
                <li key={item} className="flex gap-2">
                  <span className="mt-2 inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-accent" aria-hidden="true" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </Section>

      {/* Who it's for */}
      <Section className="bg-surface" containerClassName="max-w-3xl">
        <Eyebrow>Who it's for</Eyebrow>
        <h2 className="display-2 mt-4">Service businesses that rely on locals</h2>
        <p className="mt-5 text-lg text-ink-body">
          Contractors, trades, home services, marine, landscaping, and similar
          businesses where the customer is searching by where they live and
          what they need. Any market, any service area — the work is the same:
          earn the spot AI and Google hand to whoever they trust most in your
          category, in your town.
        </p>
      </Section>

      {/* CTA */}
      <Section containerClassName="max-w-3xl">
        <Eyebrow>Want to talk first?</Eyebrow>
        <h2 className="display-2 mt-4">Skip the audit, just reach out.</h2>
        <p className="mt-5 text-lg text-ink-body">
          If you'd rather start with a conversation than a report, tell me
          what you do, where you serve, and where you stand today. I'll
          reply with a straight read on what's worth doing first.
        </p>
        <div className="mt-8">
          <Button asChild size="lg">
            <a
              href="mailto:hello@fullcast.co?subject=Local%20SEO%20inquiry"
              onClick={handleInquiry}
            >
              Email me
            </a>
          </Button>
        </div>
      </Section>
    </MarketingLayout>
  )
}
